import React, { useState, useEffect, useCallback } from 'react';
import JSZip from 'jszip';
import {
  fetchProject, deleteProject, retryProject, downloadVideoFile, downloadVideoBlob, ensureShareToken,
  fetchActiveSpaceInfo, promoteToActiveSpace, fetchBrandProfiles, applyBrandToProject,
  fetchAudioTracks, uploadAudioTrack, updateAudioTrack, deleteAudioTrack, generateTTS,
  generateHyperDuskPreview, approveHyperDuskPreview, generateHyperDuskAnimation,
  completeImagemotionTesterIfApplicable,
  invokeGenerateMotion,
  pollImagemotionRender,
  supabase,
} from '../services/supabase';
import type { AudioTrackRow } from '../services/supabase';
import {
  DEFAULT_HYPER_DUSK_ANIMATION_STYLE,
  HYPER_DUSK_ANIMATION_STYLES,
  type HyperDuskAnimationStyle,
  type HyperDuskVariant,
  type ImagemotionProject,
  type ImagemotionProjectImage,
} from '../types';
import StatusTracker from './StatusTracker';
import VideoPlayer from './VideoPlayer';
import ShareAnalytics from './ShareAnalytics';
import DoubleCreditOffer from './DoubleCreditOffer';
import TesterConversionPanel from './TesterConversionPanel';
import { DecisionDashboard, AudioTracksPanel, VoiceRecorder, ScriptToSpeech, downloadRoyaltyFreeTrack, fetchRoyaltyFreeTracks, type LibraryTrack } from '@rehub/branded-viewer';
import type { ActiveSpaceInfo, MediaBrandProfile } from '@rehub/branded-viewer';
import { AIContentDisclosure, AIInfoPanel, persistAiProvenance } from '@rehub/ai-transparency';
import VideoEditor from './VideoEditor';
import { ArrowLeft, Loader2, Music, Image as ImageIcon, Trash2, Download, AlertTriangle, RotateCcw, Film, Check, Link2, Share2, Copy, ExternalLink, Mail, Palette, ChevronDown, ShieldAlert, Coins, Pencil } from 'lucide-react';
import { trackImagemotionEvent } from '../services/analytics';

type AppView = 'LOADING' | 'LOGIN' | 'DASHBOARD' | 'NEW_PROJECT' | 'HYPER_DUSK_PROJECT' | 'PROJECT_DETAIL';

interface ProjectDetailProps {
  projectId: string;
  userId?: string;
  isAdmin?: boolean;
  onNavigate: (view: AppView) => void;
  onOpenPlans?: () => void;
  onOpenBuyCredits?: () => void;
  doubleCreditEligible?: boolean;
  onActivationRefresh?: () => void;
  userCredits?: number;
}

function safeDownloadStem(name: string): string {
  return name.replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '') || 'project';
}

function extensionFromBlob(blob: Blob): string {
  if (blob.type.includes('png')) return 'png';
  if (blob.type.includes('webp')) return 'webp';
  if (blob.type.includes('jpeg') || blob.type.includes('jpg')) return 'jpg';
  return 'jpg';
}

function triggerLocalDownload(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  }, 2000);
}

export default function ProjectDetail({
  projectId,
  userId,
  isAdmin = false,
  onNavigate,
  onOpenPlans,
  onOpenBuyCredits,
  doubleCreditEligible = false,
  onActivationRefresh,
  userCredits,
}: ProjectDetailProps) {
  const [project, setProject] = useState<ImagemotionProject | null>(null);
  const [images, setImages] = useState<ImagemotionProjectImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [downloadingImageIndex, setDownloadingImageIndex] = useState<number | null>(null);
  const [imageDownloadError, setImageDownloadError] = useState<string | null>(null);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [activeSpaceInfo, setActiveSpaceInfo] = useState<ActiveSpaceInfo | null>(null);
  const [brands, setBrands] = useState<MediaBrandProfile[]>([]);
  const [showBrandPicker, setShowBrandPicker] = useState(false);
  const [applyingBrand, setApplyingBrand] = useState(false);
  const [audioTracks, setAudioTracks] = useState<AudioTrackRow[]>([]);
  const [audioUploading, setAudioUploading] = useState(false);
  const [libraryTracks, setLibraryTracks] = useState<LibraryTrack[]>([]);
  const [libraryAddingId, setLibraryAddingId] = useState<string | null>(null);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const [animationDurationSeconds, setAnimationDurationSeconds] = useState(5);
  const [animationStyle, setAnimationStyle] = useState<HyperDuskAnimationStyle>(DEFAULT_HYPER_DUSK_ANIMATION_STYLE);
  const [regeneratingPreview, setRegeneratingPreview] = useState(false);
  const [approvingPreview, setApprovingPreview] = useState(false);
  const [generatingAnimation, setGeneratingAnimation] = useState(false);
  const [hyperDuskActionError, setHyperDuskActionError] = useState<string | null>(null);
  const provenanceEnsuredRef = React.useRef<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);

  const isAdminView = !!(userId && project && project.user_id !== userId);
  const canDownloadImages = isAdmin || isAdminView;

  const hyperDuskImage = project?.service_type === 'hyper_dusk' ? (images[0] ?? null) : null;
  const variants = (hyperDuskImage?.dusk_variants ?? []) as HyperDuskVariant[];
  const selectedVariant = variants.find((variant) => variant.id === selectedVariantId) ?? variants[variants.length - 1] ?? null;
  const sourcePreview = hyperDuskImage?.source_image_url || hyperDuskImage?.image_url || null;
  const duskPreview = selectedVariant?.url || hyperDuskImage?.dusk_image_url || null;
  const generationStatus = hyperDuskImage?.generation_status ?? null;
  const hasDuskPreview = !!duskPreview;
  const canApprove =
    generationStatus === 'awaiting_user_approval' &&
    hasDuskPreview &&
    !approvingPreview &&
    !generatingAnimation;
  const canGenerateAnimation =
    generationStatus === 'dusk_ready' &&
    hasDuskPreview &&
    !generatingAnimation &&
    project?.status !== 'rendering';

  const buildShareUrl = (token: string) => `${window.location.origin}/view/${token}`;

  const copyText = (text: string) => {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.cssText = 'position:fixed;left:-9999px;opacity:0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    try { document.execCommand('copy'); } catch {}
    document.body.removeChild(ta);
    try { navigator.clipboard?.writeText(text); } catch {}
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  const handleShareClick = async () => {
    if (showShareMenu) { setShowShareMenu(false); return; }
    if (shareUrl) { setShowShareMenu(true); return; }
    if (project?.share_token) {
      setShareUrl(buildShareUrl(project.share_token));
      setShowShareMenu(true);
      return;
    }
    if (!project) return;
    setGeneratingToken(true);
    setShareError(null);
    try {
      const token = await ensureShareToken(project.id);
      if (token) {
        setShareUrl(buildShareUrl(token));
        setProject((prev) => prev ? { ...prev, share_token: token } : prev);
        setShowShareMenu(true);
      } else {
        setShareError('Could not generate share link');
        setShowShareMenu(true);
      }
    } catch {
      setShareError('Could not generate share link');
      setShowShareMenu(true);
    } finally {
      setGeneratingToken(false);
    }
  };

  const handleSocialShare = (platform: string) => {
    if (!shareUrl) return;
    const title = project?.name
      ? `Check out "${project.name}" — created with ImageMotion`
      : 'Check out this video — created with ImageMotion';
    const urls: Record<string, string> = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(title + '\n' + shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(shareUrl)}`,
      email: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(title + '\n\n' + shareUrl)}`,
    };
    if (platform === 'email') {
      window.open(urls[platform], '_self');
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  const handleDownloadImage = async (img: ImagemotionProjectImage, index: number) => {
    setDownloadingImageIndex(index);
    setImageDownloadError(null);
    const stem = safeDownloadStem(project?.name || 'project');
    try {
      const blob = await downloadVideoBlob(img.image_url, { storagePath: img.image_path });
      triggerLocalDownload(blob, `${stem}_image_${index + 1}.${extensionFromBlob(blob)}`);
    } catch (err) {
      console.error('Image download failed:', err);
      setImageDownloadError(err instanceof Error ? err.message : 'Image download failed. Please try again.');
      window.open(img.image_url, '_blank');
    } finally {
      setDownloadingImageIndex(null);
    }
  };

  const handleDownloadAllImages = async () => {
    if (images.length === 0) return;
    setDownloadingImages(true);
    setImageDownloadError(null);
    const stem = safeDownloadStem(project?.name || 'project');
    try {
      const zip = new JSZip();
      let added = 0;
      await Promise.all(
        images.map(async (img, i) => {
          try {
            const blob = await downloadVideoBlob(img.image_url, { storagePath: img.image_path });
            zip.file(`${stem}_image_${i + 1}.${extensionFromBlob(blob)}`, blob);
            added += 1;
          } catch (err) {
            console.error(`Failed to add image ${i + 1} to zip:`, err);
          }
        })
      );
      if (added === 0) {
        throw new Error('Could not download any images. Please try again.');
      }
      triggerLocalDownload(await zip.generateAsync({ type: 'blob' }), `${stem}_images.zip`);
    } catch (err) {
      console.error('ZIP download failed:', err);
      setImageDownloadError(err instanceof Error ? err.message : 'Could not download images. Please try again.');
    } finally {
      setDownloadingImages(false);
    }
  };

  const handleDownload = async (
    url: string,
    filename: string,
    options?: { storagePath?: string | null; ingestProject?: boolean }
  ) => {
    setDownloading(true);
    setDownloadError(null);
    try {
      await downloadVideoFile(url, filename, {
        projectId: options?.ingestProject ? projectId : undefined,
        storagePath: options?.storagePath,
      });
    } catch (err) {
      console.error('Download failed:', err);
      setDownloadError(err instanceof Error ? err.message : 'Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await retryProject(projectId);
      await load();
    } catch (err) {
      console.error('Failed to retry project:', err);
    } finally {
      setRetrying(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteProject(projectId);
      onNavigate('DASHBOARD');
    } catch (err) {
      console.error('Failed to delete project:', err);
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // Close share menu / brand picker on outside click
  useEffect(() => {
    if (!showShareMenu && !showBrandPicker) return;
    const handleClick = () => { setShowShareMenu(false); setShowBrandPicker(false); };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showShareMenu, showBrandPicker]);

  const hasPendingDecision = images.some(
    (img) => !!img.pending_clip_url || (!!img.pending_generation_id && !img.pending_clip_url),
  );

  useEffect(() => {
    if (hasPendingDecision) setEditorOpen(true);
  }, [hasPendingDecision]);

  // Ensure AI provenance exists for completed ImageMotion projects (fail-open).
  // Covers legacy projects completed before server-side write, and Railway clip gaps.
  useEffect(() => {
    if (!project || project.status !== 'completed' || !project.final_video_url) return;
    if (project.service_type === 'hyper_dusk') return;
    if (provenanceEnsuredRef.current === project.id) return;
    if (project.ai_classification) {
      provenanceEnsuredRef.current = project.id;
      return;
    }
    provenanceEnsuredRef.current = project.id;
    void (async () => {
      try {
        await persistAiProvenance({
          supabase,
          target: 'imagemotion_projects',
          id: project.id,
          app: 'imagemotion',
          operation: 'imagemotion_compose',
          sourceAssetId: project.id,
          outputAssetId: project.final_video_path || project.final_video_url || project.id,
          provider: 'shotstack',
        });
        for (const img of images) {
          if (!img.clip_url || img.ai_classification) continue;
          await persistAiProvenance({
            supabase,
            target: 'imagemotion_project_images',
            id: img.id,
            app: 'imagemotion',
            operation: 'imagemotion_clip',
            sourceAssetId: img.source_image_path || img.source_image_url || img.image_path || img.id,
            outputAssetId: img.clip_url,
            provider: 'fal',
          });
        }
      } catch (err) {
        console.warn('ImageMotion provenance ensure failed (non-fatal):', err);
      }
    })();
  }, [project, images]);

  // Mark tester complete + unlock double-credit offer when a Tester Video finishes.
  useEffect(() => {
    if (!project || project.status !== 'completed' || project.quality !== 'tester') return;
    void completeImagemotionTesterIfApplicable(project.id)
      .then(() => onActivationRefresh?.())
      .catch(() => {});
    trackImagemotionEvent('imagemotion_creation_completed', userId, {
      package_id: 'tester',
      offer_eligible: doubleCreditEligible,
    });
  }, [project?.id, project?.status, project?.quality, userId, doubleCreditEligible, onActivationRefresh]);

  const handleApplyBrand = async (brandId: string | null) => {
    setApplyingBrand(true);
    try {
      await applyBrandToProject(projectId, brandId);
      setProject(prev => prev ? { ...prev, brand_profile_id: brandId } : prev);
    } catch (err) {
      console.error('Failed to apply brand:', err);
    } finally {
      setApplyingBrand(false);
      setShowBrandPicker(false);
    }
  };

  const load = useCallback(async () => {
    const result = await fetchProject(projectId);
    if (!result.project) {
      setError('Project not found');
      setLoading(false);
      return;
    }
    setProject(result.project);
    setImages(result.images);
    setLoading(false);
    if (result.project?.user_id) {
      fetchActiveSpaceInfo(result.project.user_id).then(setActiveSpaceInfo);
      fetchBrandProfiles(result.project.user_id).then(setBrands);
    }
    fetchAudioTracks(projectId).then(setAudioTracks);
    fetchRoyaltyFreeTracks(supabase).then(setLibraryTracks);
  }, [projectId]);

  const handleUploadAudioTrack = useCallback(
    async (file: File, trackType: 'music' | 'voiceover', label?: string) => {
      if (!userId) return;
      setAudioUploading(true);
      try {
        const track = await uploadAudioTrack(file, trackType, userId, projectId, label);
        setAudioTracks((prev) => [...prev, track]);
      } finally {
        setAudioUploading(false);
      }
    },
    [userId, projectId]
  );

  const handleUseLibraryTrack = useCallback(
    async (track: LibraryTrack) => {
      if (!userId) return;
      setLibraryAddingId(track.id);
      setAudioUploading(true);
      try {
        const file = await downloadRoyaltyFreeTrack(supabase, track);
        const saved = await uploadAudioTrack(file, 'music', userId, projectId, track.title);
        setAudioTracks((prev) => [...prev, saved]);
      } finally {
        setLibraryAddingId(null);
        setAudioUploading(false);
      }
    },
    [userId, projectId]
  );

  const handleUpdateAudioTrack = useCallback(
    async (trackId: string, updates: Partial<Pick<AudioTrackRow, 'volume' | 'fade_in' | 'fade_out' | 'is_active' | 'label'>>) => {
      setAudioTracks((prev) =>
        prev.map((t) => (t.id === trackId ? { ...t, ...updates, updated_at: new Date().toISOString() } : t))
      );
      await updateAudioTrack(trackId, updates);
    },
    []
  );

  const handleDeleteAudioTrack = useCallback(
    async (trackId: string) => {
      setAudioTracks((prev) => prev.filter((t) => t.id !== trackId));
      await deleteAudioTrack(trackId);
    },
    []
  );

  const handleRecordingComplete = useCallback(
    async (blob: Blob) => {
      if (!userId) return;
      setAudioUploading(true);
      try {
        const file = new File([blob], `recording-${Date.now()}.webm`, { type: blob.type });
        const track = await uploadAudioTrack(file, 'voiceover', userId, projectId, 'Recorded voiceover', 'recording');
        setAudioTracks((prev) => [...prev, track]);
      } finally {
        setAudioUploading(false);
      }
    },
    [userId, projectId]
  );

  const handleGenerateTTS = useCallback(
    async (params: { text: string; provider: 'falai' | 'elevenlabs'; voice_id?: string }) => {
      const track = await generateTTS({
        ...params,
        project_id: projectId,
      });
      setAudioTracks((prev) => [...prev, track]);
    },
    [projectId]
  );

  useEffect(() => {
    if (!hyperDuskImage) return;
    const nextVariantId =
      hyperDuskImage.selected_dusk_variant_id ||
      variants[variants.length - 1]?.id ||
      null;
    setSelectedVariantId((current) => current ?? nextVariantId);
  }, [hyperDuskImage?.id, hyperDuskImage?.selected_dusk_variant_id, variants]);

  useEffect(() => {
    if (!hyperDuskImage) return;
    setAnimationDurationSeconds(
      Math.max(5, Math.min(10, Math.round(hyperDuskImage.clip_duration || 5)))
    );
    if (hyperDuskImage.kling_animation_style) {
      setAnimationStyle(hyperDuskImage.kling_animation_style);
    }
  }, [hyperDuskImage?.id]);

  useEffect(() => {
    if (!hyperDuskImage) return;
    const lockedStatuses = new Set(['kling_processing', 'animation_ready', 'completed']);
    if (!lockedStatuses.has(hyperDuskImage.generation_status ?? '')) return;
    setAnimationDurationSeconds(
      Math.max(5, Math.min(10, Math.round(hyperDuskImage.clip_duration || 5)))
    );
    if (hyperDuskImage.kling_animation_style) {
      setAnimationStyle(hyperDuskImage.kling_animation_style);
    }
  }, [
    hyperDuskImage?.clip_duration,
    hyperDuskImage?.generation_status,
    hyperDuskImage?.kling_animation_style,
  ]);

  const handleRegenerateHyperDuskPreview = async () => {
    setRegeneratingPreview(true);
    setHyperDuskActionError(null);
    try {
      await generateHyperDuskPreview(projectId);
      await load();
    } catch (err) {
      setHyperDuskActionError(err instanceof Error ? err.message : 'Failed to regenerate dusk preview');
    } finally {
      setRegeneratingPreview(false);
    }
  };

  const handleApproveHyperDuskPreview = async () => {
    if (!hyperDuskImage?.id) return;
    setApprovingPreview(true);
    setHyperDuskActionError(null);
    try {
      await approveHyperDuskPreview(projectId, hyperDuskImage.id, selectedVariant?.id);
      await load();
    } catch (err) {
      setHyperDuskActionError(err instanceof Error ? err.message : 'Failed to approve dusk preview');
    } finally {
      setApprovingPreview(false);
    }
  };

  const handleGenerateHyperDuskAnimation = async () => {
    if (!hasDuskPreview) return;
    setGeneratingAnimation(true);
    setHyperDuskActionError(null);
    try {
      await generateHyperDuskAnimation(projectId, {
        durationSeconds: animationDurationSeconds,
        animationStyle,
        selectedVariantId: selectedVariant?.id,
      });
      await load();
    } catch (err) {
      setHyperDuskActionError(err instanceof Error ? err.message : 'Failed to generate animation');
    } finally {
      setGeneratingAnimation(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    load();
  }, [load]);

  // Poll while clips are generating or a final render is in progress.
  useEffect(() => {
    if (!project) return;
    const shouldPoll =
      project.status === 'queued' ||
      project.status === 'generating_clips' ||
      project.status === 'rendering';
    if (!shouldPoll) return;

    let cancelled = false;
    let inFlight = false;

    const tick = async () => {
      if (cancelled || inFlight) return;
      inFlight = true;
      try {
        if (project.motion_tier_id) {
          if (project.status === 'queued' || project.status === 'editing') {
            try {
              await invokeGenerateMotion({ projectId });
            } catch (err) {
              console.warn('Motion submit retry error:', err);
            }
          } else if (project.status === 'generating_clips') {
            try {
              await invokeGenerateMotion({ projectId, action: 'poll' });
            } catch (err) {
              console.warn('Motion poll error:', err);
            }
          }
        }
        if (project.status === 'rendering') {
          try {
            await pollImagemotionRender(projectId);
          } catch (err) {
            console.warn('Render poll error:', err);
          }
        }
        if (!cancelled) await load();
      } finally {
        inFlight = false;
      }
    };

    void tick();
    const interval = setInterval(tick, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    project?.status,
    project?.motion_tier_id,
    load,
    projectId,
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="text-center py-20">
        <p className="text-red-400">{error ?? 'Project not found'}</p>
        <button
          onClick={() => onNavigate('DASHBOARD')}
          className="inline-flex items-center gap-2 mt-4 text-blue-400 hover:underline"
        >
          <ArrowLeft className="w-4 h-4" /> Back to projects
        </button>
      </div>
    );
  }

  const hasEditableClips = images.some((img) => !!img.clip_url);
  const showEditVideoButton =
    project.status === 'completed' && hasEditableClips && !editorOpen;
  const editVideoButton = showEditVideoButton ? (
    <button
      type="button"
      onClick={() => setEditorOpen(true)}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold shadow-lg border transition-colors ${
        hasPendingDecision
          ? 'bg-indigo-600 hover:bg-indigo-500 shadow-indigo-500/30 border-indigo-400/40'
          : 'bg-purple-600 hover:bg-purple-500 shadow-purple-500/30 border-purple-400/40'
      }`}
    >
      <Pencil className="w-4 h-4" />
      {hasPendingDecision ? 'Review new clip' : 'Edit video'}
    </button>
  ) : null;

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <button
          onClick={() => onNavigate('DASHBOARD')}
          className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-blue-400 mb-4 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> All Projects
        </button>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-white">
            {project.name || 'Untitled Project'}
          </h1>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500">
              Created {new Date(project.created_at).toLocaleDateString()}
            </span>
            {/* Delete button */}
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="p-2 text-slate-500 hover:text-red-400 rounded-lg hover:bg-slate-800 transition-colors"
                title="Delete project"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  {deleting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    'Delete'
                  )}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="px-3 py-1.5 bg-slate-700 text-slate-300 text-xs font-medium rounded-md hover:bg-slate-600 transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {isAdminView && (
        <div className="flex items-center gap-3 rounded-lg border border-amber-600/40 bg-amber-900/20 px-4 py-3">
          <ShieldAlert className="w-5 h-5 text-amber-400 shrink-0" />
          <div>
            <p className="text-sm font-medium text-amber-300">Admin View</p>
            <p className="text-xs text-amber-400/70">This project belongs to another user. You're viewing it as an admin.</p>
          </div>
        </div>
      )}

      {/* Status tracker */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
        <h2 className="text-sm font-medium text-slate-400 mb-4">
          Processing Status
        </h2>
        <StatusTracker
          status={project.status}
          errorMessage={project.error_message}
          serviceType={project.service_type}
          generationStatus={images[0]?.generation_status ?? null}
        />
      </div>

      {project.service_type === 'hyper_dusk' && hyperDuskImage && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-4">
          <h2 className="text-sm font-medium text-slate-400">Review & Approve Night Version</h2>

          {hyperDuskActionError && (
            <div className="p-3 rounded-lg bg-red-900/20 border border-red-900/50 text-red-300 text-sm">
              {hyperDuskActionError}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Original</p>
              <div className="aspect-video rounded bg-slate-900 flex items-center justify-center overflow-hidden">
                {sourcePreview ? (
                  <img src={sourcePreview} alt="Original upload" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs text-slate-500">No source image</span>
                )}
              </div>
            </div>
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-3">
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Night Version</p>
              <div className="aspect-video rounded bg-slate-900 flex items-center justify-center overflow-hidden">
                {duskPreview ? (
                  <img src={duskPreview} alt="Generated night version" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xs text-slate-500">Generating preview...</span>
                )}
              </div>
            </div>
          </div>

          {variants.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-slate-400">Saved Variants ({variants.length})</p>
                <p className="text-xs text-slate-500">Select the version to approve or animate</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {variants.map((variant, index) => {
                  const isSelected = variant.id === selectedVariant?.id;
                  return (
                    <button
                      key={variant.id}
                      type="button"
                      onClick={() => setSelectedVariantId(variant.id)}
                      className={`rounded-lg border p-2 text-left transition-colors ${
                        isSelected
                          ? 'border-indigo-500 bg-indigo-900/20'
                          : 'border-slate-700 bg-slate-800/40 hover:border-slate-600'
                      }`}
                    >
                      <div className="aspect-video rounded overflow-hidden bg-slate-900 flex items-center justify-center">
                        {variant.url ? (
                          <img src={variant.url} alt={`Variant ${index + 1}`} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-[10px] text-slate-500">No preview</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-300 mt-2 font-medium">Variant {index + 1}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-3 border-t border-slate-800 pt-4">
            <button
              type="button"
              onClick={handleRegenerateHyperDuskPreview}
              disabled={regeneratingPreview || approvingPreview || generatingAnimation}
              className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 rounded-lg hover:bg-slate-700 disabled:opacity-50 transition-colors"
            >
              {regeneratingPreview ? 'Regenerating...' : 'Regenerate Night Version'}
            </button>

            {generationStatus === 'awaiting_user_approval' && (
              <button
                type="button"
                onClick={handleApproveHyperDuskPreview}
                disabled={!canApprove}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                {approvingPreview ? 'Approving...' : 'Approve Night Version'}
              </button>
            )}

            {(generationStatus === 'dusk_ready' || generationStatus === 'kling_processing') && (
              <div className="flex items-center gap-2 ml-auto">
                <label className="text-xs text-slate-400">Duration</label>
                <select
                  value={animationDurationSeconds}
                  onChange={(e) => setAnimationDurationSeconds(Number(e.target.value))}
                  className="px-2 py-1 text-xs border border-slate-700 rounded bg-slate-800 text-white"
                >
                  {[5, 6, 7, 8, 9, 10].map((duration) => (
                    <option key={duration} value={duration}>{duration}s</option>
                  ))}
                </select>
                <label className="text-xs text-slate-400 ml-2">Motion</label>
                <select
                  value={animationStyle}
                  onChange={(e) => setAnimationStyle(e.target.value as HyperDuskAnimationStyle)}
                  className="px-2 py-1 text-xs border border-slate-700 rounded bg-slate-800 text-white"
                >
                  {HYPER_DUSK_ANIMATION_STYLES.map((style) => (
                    <option key={style.value} value={style.value}>{style.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={handleGenerateHyperDuskAnimation}
                  disabled={!canGenerateAnimation}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:cursor-not-allowed ${
                    generatingAnimation || generationStatus === 'kling_processing'
                      ? 'bg-indigo-600 ring-2 ring-indigo-400/60 shadow-lg shadow-indigo-500/30 animate-pulse'
                      : 'bg-green-600 hover:bg-green-700 disabled:opacity-50'
                  }`}
                >
                  {generatingAnimation || generationStatus === 'kling_processing' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Generating Animation...
                    </>
                  ) : (
                    `Generate ${animationDurationSeconds}s Animation`
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Retry button for failed projects */}
      {project.status === 'failed' && (
        <div className="flex items-center gap-3">
          <button
            onClick={handleRetry}
            disabled={retrying}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {retrying ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RotateCcw className="w-4 h-4" />
            )}
            {retrying ? 'Retrying...' : 'Retry Processing'}
          </button>
          <span className="text-xs text-slate-500">
            Requeues the project for processing
          </span>
        </div>
      )}

      {(() => {
        const showEditor =
          (project.status === 'editing' ||
            project.status === 'rendering' ||
            editorOpen ||
            (project.status === 'completed' && (editorOpen || hasPendingDecision))) &&
          hasEditableClips;
        const showFinalOnlyNote =
          project.status === 'completed' &&
          !!project.final_video_url &&
          !hasEditableClips;

        return (
          <>
            {showEditor && (
              <VideoEditor
                project={project}
                images={images}
                onReload={load}
                onOpenBuyCredits={onOpenBuyCredits}
              />
            )}
            {showEditVideoButton && !project.final_video_url && (
              <div className="flex justify-end">{editVideoButton}</div>
            )}
            {showFinalOnlyNote && (
              <div className="flex items-start gap-3 bg-slate-900 border border-slate-800 rounded-lg p-4">
                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-300">
                  This older project only has a finished video. Individual clips were not kept, so Review & edit is unavailable.
                </p>
              </div>
            )}
          </>
        );
      })()}

      {/* Warning for partial completions (some images skipped) */}
      {project.status === 'completed' && project.error_message && (
        <div className="flex items-start gap-3 bg-yellow-900/30 border border-yellow-700/50 rounded-lg p-4">
          <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-300">{project.error_message}</p>
        </div>
      )}

      {/* Video player (when completed, or previous export during a re-render) */}
      {(project.status === 'completed' || (project.status === 'rendering' && project.final_video_url)) && project.final_video_url && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-white">Your Video</h2>
              <AIContentDisclosure
                classification="AI_GENERATED"
                operation="imagemotion_compose"
                app="imagemotion"
              />
            </div>
            <div className="flex items-center gap-2">
              {editVideoButton}
              <div className="relative">
                <button
                  onClick={(e) => { e.stopPropagation(); handleShareClick(); }}
                  disabled={generatingToken}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-600 disabled:opacity-50 transition-colors"
                >
                  {generatingToken ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                  {generatingToken ? 'Generating...' : 'Share'}
                </button>

                {showShareMenu && (
                  <div
                    className="absolute right-0 top-full mt-2 w-64 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl shadow-black/50 z-50 overflow-hidden animate-fade-in"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {shareError && (
                      <div className="px-4 py-2.5 text-xs text-red-400 bg-red-900/20 border-b border-slate-700">
                        {shareError}
                      </div>
                    )}
                    {shareUrl && (
                      <>
                        {/* Visible URL the user can manually select/copy */}
                        <div className="px-3 py-2 border-b border-slate-700 bg-slate-900/50">
                          <input
                            type="text"
                            readOnly
                            value={shareUrl}
                            onFocus={(e) => e.target.select()}
                            className="w-full text-xs text-slate-300 bg-transparent outline-none select-all font-mono"
                          />
                        </div>
                        <button
                          onClick={() => copyText(shareUrl)}
                          className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-slate-700 transition-colors border-b border-slate-700"
                        >
                          {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4 text-slate-400" />}
                          {copied ? 'Link copied!' : 'Copy link'}
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleSocialShare('whatsapp')}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-slate-700 transition-colors"
                    >
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-slate-400" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      WhatsApp
                    </button>
                    <button
                      onClick={() => handleSocialShare('facebook')}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-slate-700 transition-colors"
                    >
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-slate-400" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                      Facebook
                    </button>
                    <button
                      onClick={() => handleSocialShare('twitter')}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-slate-700 transition-colors"
                    >
                      <svg viewBox="0 0 24 24" className="w-4 h-4 text-slate-400" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      X (Twitter)
                    </button>
                    <button
                      onClick={() => handleSocialShare('email')}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-white hover:bg-slate-700 transition-colors"
                    >
                      <Mail className="w-4 h-4 text-slate-400" />
                      Email
                    </button>
                    
                  </div>
                )}
              </div>
              <button
                onClick={() => handleDownload(
                  project.final_video_url!,
                  `ImageMotion Video - ${project.name || 'Untitled'}.mp4`,
                  { storagePath: project.final_video_path, ingestProject: true }
                )}
                disabled={downloading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {downloading ? 'Downloading...' : 'Download'}
              </button>
              {project.settings?.photoplan_return_url && (
                <a
                  href={
                    project.settings.photoplan_return_url.includes('deployImagemotion=')
                      ? project.settings.photoplan_return_url
                      : `${project.settings.photoplan_return_url}${project.settings.photoplan_return_url.includes('?') ? '&' : '?'}deployImagemotion=1`
                  }
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Deploy to Photoplan job
                </a>
              )}
            </div>
          </div>
          {downloadError && (
            <p className="mb-3 text-sm text-red-400 flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {downloadError}
            </p>
          )}
          <VideoPlayer
            url={project.final_video_url}
            aspectRatio={project.settings?.aspect_ratio}
          />
          <div className="mt-4">
            <AIInfoPanel
              classification="AI_GENERATED"
              operation="imagemotion_compose"
              app="imagemotion"
              hasSource
              createdAt={project.updated_at || project.created_at}
            />
          </div>

          {project.quality === 'tester' && project.status === 'completed' && userId && !isAdminView && typeof userCredits === 'number' && (
            <div className="mt-6">
              <TesterConversionPanel
                userId={userId}
                projectId={project.id}
                userCredits={userCredits}
                onCreateFullVideo={() => onNavigate('NEW_PROJECT')}
              />
            </div>
          )}

          {project.quality === 'tester' && doubleCreditEligible && userId && (
            <div className="mt-6 space-y-4">
              <DoubleCreditOffer
                userId={userId}
                currency="GBP"
                processingId={null}
                variant="completion"
                onSelect={() => {
                  onOpenBuyCredits?.();
                }}
              />
              <button
                type="button"
                onClick={() => {
                  trackImagemotionEvent('buy_credits_clicked', userId, {
                    source: 'tester_completion',
                    offer_eligible: true,
                    package_id: 'tester',
                  });
                  onOpenBuyCredits?.();
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-500 text-white text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400"
              >
                <Coins className="w-4 h-4" />
                Buy credits
              </button>
            </div>
          )}
        </div>
      )}

      {/* Audio Tracks (when completed) */}
      {project.status === 'completed' && project.final_video_url && userId && (
        <AudioTracksPanel
          tracks={audioTracks}
          onUploadTrack={handleUploadAudioTrack}
          onUpdateTrack={handleUpdateAudioTrack}
          onDeleteTrack={handleDeleteAudioTrack}
          uploading={audioUploading}
          libraryTracks={libraryTracks}
          onUseLibraryTrack={handleUseLibraryTrack}
          libraryAddingId={libraryAddingId}
          supabase={supabase}
          renderVoiceoverSection={
            <>
              <VoiceRecorder onRecordingComplete={handleRecordingComplete} />
              <ScriptToSpeech
                onGenerate={handleGenerateTTS}
                availableProviders={['falai']}
              />
            </>
          }
        />
      )}

      {/* Brand Picker */}
      {project.status === 'completed' && brands.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-300">Viewer Brand</span>
            </div>
            <div className="relative" onClick={e => e.stopPropagation()}>
              <button
                onClick={() => setShowBrandPicker(!showBrandPicker)}
                disabled={applyingBrand}
                className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 hover:border-slate-600 transition-colors disabled:opacity-50"
              >
                {applyingBrand ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <>
                    {(() => {
                      const current = brands.find(b => b.id === project.brand_profile_id);
                      return current ? (
                        <span className="flex items-center gap-2">
                          {current.logo_url && <img src={current.logo_url} alt="" className="w-4 h-4 rounded object-contain" />}
                          {current.name}
                        </span>
                      ) : (
                        <span className="text-slate-500">No brand selected</span>
                      );
                    })()}
                    <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                  </>
                )}
              </button>

              {showBrandPicker && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-slate-800 border border-slate-700 rounded-xl shadow-2xl shadow-black/60 z-50 overflow-hidden">
                  <button
                    onClick={() => handleApplyBrand(null)}
                    className={`w-full flex items-center gap-2 px-3 py-2.5 text-xs text-left hover:bg-slate-700 transition-colors ${
                      !project.brand_profile_id ? 'text-blue-400' : 'text-slate-400'
                    }`}
                  >
                    {!project.brand_profile_id && <Check className="w-3 h-3" />}
                    <span className={!project.brand_profile_id ? '' : 'ml-5'}>No brand (account default)</span>
                  </button>
                  {brands.map(brand => (
                    <button
                      key={brand.id}
                      onClick={() => handleApplyBrand(brand.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 text-xs text-left hover:bg-slate-700 transition-colors ${
                        project.brand_profile_id === brand.id ? 'text-blue-400' : 'text-slate-300'
                      }`}
                    >
                      {project.brand_profile_id === brand.id && <Check className="w-3 h-3" />}
                      <span className={`flex items-center gap-2 ${project.brand_profile_id !== brand.id ? 'ml-5' : ''}`}>
                        {brand.logo_url && <img src={brand.logo_url} alt="" className="w-4 h-4 rounded object-contain" />}
                        <span className="truncate">{brand.name}</span>
                        {brand.is_default && <span className="text-[9px] text-purple-400 bg-purple-900/30 px-1 rounded">default</span>}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <p className="text-[11px] text-slate-600 mt-1.5">Choose which brand appears on the shared viewer for this project</p>
        </div>
      )}

      {/* Decision Dashboard: download vs promote */}
      {project.status === 'completed' && project.final_video_url && (
        <DecisionDashboard
          projectName={project.name || 'Untitled'}
          videoUrl={project.final_video_url}
          isPromoted={project.is_promoted}
          daysRemaining={Math.max(0, 30 - Math.floor((Date.now() - new Date(project.created_at).getTime()) / (1000 * 60 * 60 * 24)))}
          activeSpaceInfo={activeSpaceInfo}
          onDownload={() => handleDownload(
            project.final_video_url!,
            `ImageMotion Video - ${project.name || 'Untitled'}.mp4`,
            { storagePath: project.final_video_path, ingestProject: true }
          )}
          onPromote={() => promoteToActiveSpace(project.id)}
          onOpenPlans={onOpenPlans}
          downloading={downloading}
          primaryColor="#3b82f6"
          secondaryColor="#7c3aed"
        />
      )}

      {/* Share Analytics (completed projects only) */}
      {project.status === 'completed' && (
        <ShareAnalytics
          projectId={project.id}
          shareToken={project.share_token}
        />
      )}

      {/* Generated Clips */}
      {images.some((img) => img.clip_url) && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Film className="w-5 h-5 text-slate-500" />
            <h2 className="text-sm font-medium text-slate-400">
              Generated Clips ({images.filter((img) => img.clip_url).length}/{images.length})
            </h2>
            <AIContentDisclosure
              classification="AI_GENERATED"
              operation="imagemotion_clip"
              app="imagemotion"
              compact
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {images.map((img, index) => (
              <div key={img.id} className="relative">
                {img.clip_url ? (
                  <div className="rounded-lg overflow-hidden border border-slate-700 bg-black">
                    <video
                      src={img.clip_url}
                      className="w-full aspect-video object-cover"
                      muted
                      playsInline
                      onMouseEnter={(e) => (e.target as HTMLVideoElement).play()}
                      onMouseLeave={(e) => { const v = e.target as HTMLVideoElement; v.pause(); v.currentTime = 0; }}
                    />
                    <div className="flex items-center justify-between px-2 py-1.5 bg-slate-800/80">
                      <span className="text-[10px] text-slate-400">Clip {index + 1}</span>
                      <button
                        onClick={() => handleDownload(img.clip_url!, `clip-${index + 1}.mp4`)}
                        className="flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        <Download className="w-3 h-3" />
                        Download
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="aspect-video rounded-lg bg-slate-800 flex items-center justify-center border border-slate-700">
                    <span className="text-[10px] text-slate-600">No clip</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Images */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-slate-500" />
            <h2 className="text-sm font-medium text-slate-400">
              Images ({images.length})
            </h2>
          </div>
          {canDownloadImages && images.length > 0 && (
            <button
              type="button"
              onClick={handleDownloadAllImages}
              disabled={downloadingImages}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors"
            >
              {downloadingImages ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              {downloadingImages ? 'Downloading...' : 'Download All'}
            </button>
          )}
        </div>
        {imageDownloadError && (
          <div className="mb-3 p-3 rounded-lg bg-red-900/20 border border-red-900/50 text-red-300 text-sm">
            {imageDownloadError}
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((img, index) => (
            <div key={img.id} className="relative aspect-[4/3] rounded-lg overflow-hidden border border-slate-700 bg-slate-800">
              <img
                src={img.image_url}
                alt={`Image ${index + 1}`}
                className="w-full h-full object-contain"
              />
              <span className="absolute top-1 left-1 w-5 h-5 bg-blue-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {index + 1}
              </span>
              {canDownloadImages && (
                <button
                  type="button"
                  onClick={() => handleDownloadImage(img, index)}
                  disabled={downloadingImageIndex === index || downloadingImages}
                  className="absolute bottom-1.5 right-1.5 inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black/70 text-[10px] text-white hover:bg-blue-600 disabled:opacity-50 transition-colors"
                  title={`Download image ${index + 1}`}
                >
                  {downloadingImageIndex === index ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Download className="w-3 h-3" />
                  )}
                  Download
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Settings summary */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6">
        <h2 className="text-sm font-medium text-slate-400 mb-4">
          Project Settings
        </h2>
        {(() => {
          const firstImage = images[0];
          const variantCount = firstImage?.dusk_variants?.length ?? 0;
          const animationDuration = firstImage?.clip_duration ?? project.settings?.clip_duration ?? 5;
          const animationStyle = firstImage?.kling_animation_style ?? 'gentle_dolly';
          return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-slate-500">Service</span>
            <p className="mt-1 font-medium text-white">
              {project.service_type === 'hyper_dusk' ? 'Hyper Dusk' : 'Standard Motion'}
            </p>
          </div>
          <div>
            <span className="text-slate-500">
              <Music className="w-4 h-4 inline mr-1" />
              Music
            </span>
            <p className="mt-1 font-medium text-white">
              {project.music_url ? 'Yes' : 'None'}
            </p>
          </div>
          <div>
            <span className="text-slate-500">Logo</span>
            <p className="mt-1 font-medium text-white">
              {project.logo_url ? 'Yes' : 'None'}
            </p>
          </div>
          <div>
            <span className="text-slate-500">Transition</span>
            <p className="mt-1 font-medium text-white capitalize">
              {project.transition_type}
            </p>
          </div>
          {project.service_type === 'hyper_dusk' && (
            <>
              <div>
                <span className="text-slate-500">Dusk Variants</span>
                <p className="mt-1 font-medium text-white">{variantCount}</p>
              </div>
              <div>
                <span className="text-slate-500">Animation Length</span>
                <p className="mt-1 font-medium text-white">{Math.max(5, Math.min(10, Math.round(animationDuration)))}s</p>
              </div>
              <div>
                <span className="text-slate-500">Animation Style</span>
                <p className="mt-1 font-medium text-white capitalize">
                  {String(animationStyle).replace(/_/g, ' ')}
                </p>
              </div>
            </>
          )}
        </div>
          );
        })()}
      </div>
    </div>
  );
}
